import { ReactNode, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { OfficeBuildingOutline, SparklesOutline, UserOutline, UsersOutline } from '@bonusly/design-system';
import { Permission, useCurrentUser, usePermission } from '@bonusly/who-am-i';

import { FilterType, useRecognitionFeedFilterOptions } from '../api/hooks/useRecognitionFeedFilterOptions';
import { SavedFilterOption, useSavedFilterOptions } from '../components/BonusFeed/hooks/useSavedFilterOptions';
import { useTransformSavedFilterOption } from './useTransformSavedFeedType';

// Types that are available in the feed type dropdown
export enum FeedType {
	Company = 'company',
	Department = 'department',
	Location = 'location',
	Relevant = 'relevant',
	Team = 'team',
	YourActivity = 'your_activity',
}

export interface FeedTypeOption {
	id: string; // matcher for the feed type dropdown since none of the other fields are unique
	displayLabel: string;
	icon: ReactNode;
	managerId?: string;
	feedType: FeedType;
}

export const useFeedTypeOptions = () => {
	const { t } = useTranslation('recognition');
	const user = useCurrentUser();
	const canAccessUserSavedFeeds = usePermission(Permission.CanAccessUserSavedFeeds);
	const { data: filterOptions, isLoading: isLoadingFilterOptions } = useRecognitionFeedFilterOptions();
	const { data: userSavedFeeds } = useSavedFilterOptions();
	const transformSavedFilterOption = useTransformSavedFilterOption();

	const isManager = user?.is_manager || false;
	const hasManager = !!user?.manager_id;
	const managerDisplayName = user?.manager_display_name;
	// NOTE: Team option is different based on if user is a manager or has a manager
	// If manager: id is user's own ID (shows their direct reports)
	// If not manager: id is manager's ID (shows everyone who shares the same manager)
	const canAccessTeam = (isManager && user?.id) || (hasManager && user?.manager_id);
	// NOTE: If user is a manager AND has a manager, show their manager's team as well
	const canAccessManagersTeam = isManager && hasManager && user?.manager_id && managerDisplayName;
	// NOTE: If the true value is in the options for the relevance filter option then the user can access the relevant feed type
	const canAccessRelevant = !!filterOptions?.find((option) => option.filter_type === FilterType.Relevance)?.options.find((option) => option.value === true);

	const baseFeedTypeOptions: FeedTypeOption[] = useMemo(() => {
		const options: FeedTypeOption[] = [];

		// RELEVANT
		if (canAccessRelevant) {
			options.push({ id: 'relevant', feedType: FeedType.Relevant, icon: SparklesOutline({ size: 'sm' }), displayLabel: t('bonus_feed.filters.relevant') });
		}

		// TEAM
		if (canAccessTeam) {
			options.push({
				id: 'team',
				feedType: FeedType.Team,
				icon: UsersOutline({ size: 'sm' }),
				managerId: isManager ? user?.id : user?.manager_id,
				displayLabel: t('bonus_feed.filters.team'),
			});
		}

		// MANAGERS TEAM
		if (canAccessManagersTeam) {
			options.push({
				id: 'managers-team',
				feedType: FeedType.Team,
				icon: UsersOutline({ size: 'sm' }),
				managerId: user.manager_id,
				displayLabel: t('bonus_feed.filters.team_option_text', { name: managerDisplayName }),
			});
		}

		// COMPANY
		options.push({ id: 'company', feedType: FeedType.Company, icon: OfficeBuildingOutline({ size: 'sm' }), displayLabel: t('bonus_feed.filters.company') });

		// YOUR ACTIVITY
		options.push({
			id: 'your_activity',
			feedType: FeedType.YourActivity,
			icon: UserOutline({ size: 'sm' }),
			displayLabel: t('bonus_feed.filters.your_activity'),
		});

		return options;
	}, [canAccessRelevant, canAccessTeam, canAccessManagersTeam, managerDisplayName, t, isManager, user?.id, user?.manager_id]);

	const userSavedFeedOptions: FeedTypeOption[] = useMemo(() => {
		if (!canAccessUserSavedFeeds || !userSavedFeeds) {
			return [];
		}
		return userSavedFeeds.map((savedFilterOption: SavedFilterOption) => transformSavedFilterOption(savedFilterOption));
	}, [canAccessUserSavedFeeds, userSavedFeeds, transformSavedFilterOption]);

	const allFeedTypeOptions = useMemo(() => [...baseFeedTypeOptions, ...userSavedFeedOptions], [baseFeedTypeOptions, userSavedFeedOptions]);

	const defaultFeedTypeOption = useMemo(() => {
		// check user?.feed_preference for the default selected option
		// if it is not found, default to relevant, fallback is company
		const defaultFeedType = user?.feed_preference;
		// NOTE: default feed is saved by the ID not the feedType since feedType is not unique
		const savedOption = allFeedTypeOptions.find((option) => option.id === defaultFeedType);
		const relevantOption = allFeedTypeOptions.find((option) => option.feedType === FeedType.Relevant);
		const companyOption = allFeedTypeOptions.find((option) => option.feedType === FeedType.Company);
		return savedOption || relevantOption || companyOption;
	}, [allFeedTypeOptions, user?.feed_preference]);

	return {
		allFeedTypeOptions,
		baseFeedTypeOptions,
		defaultFeedTypeOption,
		isLoadingFilterOptions,
		userSavedFeedOptions,
	};
};
